import { PDFDocument } from 'pdf-lib'
import html2canvas from 'html2canvas'
import jsPDF from 'jspdf'
import request from 'utils/request'


// NOTE (Chrome ≥118 PDF print behavior, not only chrome but other browsers too):
// Chrome's PDF viewer (PDFium) will only honor window.print() when it is invoked
// synchronously from a trusted user gesture in the top-level browsing context.
// As a result, the following approaches will NOT reliably open the print dialog:
// - Printing from hidden/inline iframes (PDF runs sandboxed; print() ignored)
// - Printing from async callbacks/promises (not a direct user gesture)
// - Printing blob:/data: URLs inside iframes/embeds (treated as cross-origin/sandboxed)
// Therefore, to ensure the print dialog opens, we must open the PDF in a new top-level
// window synchronously within the click handler, then call print() there when it loads.
// This function implements that approach intentionally.
async function printMergedPdf(blob) {
  // open synchronously inside user click
  const printWindow = window.open('', '_blank');
  if (!printWindow) {
    alert('Please allow pop-ups for printing.');
    return;
  }

  // show temporary message
  printWindow.document.write('<p style="font-family:sans-serif">Preparing document…</p>');

  // convert blob → object URL and print once it loads
  const blobUrl = URL.createObjectURL(blob);
  printWindow.location = blobUrl;
  printWindow.onload = () => {
    printWindow.focus();
    printWindow.print();
    setTimeout(() => {
      URL.revokeObjectURL(blobUrl);
      printWindow.close();
    }, 1000);
  };
}


export async function mergeAndPrint({ pdfUrl, consentName, consentId, consentVersion, dispatch }) {
  try {
    // Create a print-only signature page in offscreen DOM
    const container = document.createElement('div')
    container.style.position = 'fixed'
    container.style.left = '-10000px'
    container.style.top = '0'
    container.style.width = '595px' // A4 width in pt (~ CSS px for html2canvas)
    container.style.minHeight = '842px'
    container.style.background = '#ffffff'
    container.style.color = '#000'
    container.style.display = 'block'
    container.innerHTML = `
      <div style="min-height:842px;padding:72px 64px 64px 64px;font-family:'Times New Roman',serif;background:#fff;color:#000;">
        <div style="max-width:680px;margin:0 auto;">
          <div style="text-align:center;margin-bottom:72px;">
            <h2 style="font-size:20px;font-weight:bold;margin:0;color:#000;">${consentName || 'Consent Form'}</h2>
          </div>

          <!-- Print Name row: line above the label -->
          <div style="margin:32px 0 56px 0;">
            <div style="border-bottom:1px solid #000;height:1px;width:60%;margin:0 0 8px 0;"></div>
            <div style="font-size:14px;text-align:left;">Print Name</div>
          </div>

          <!-- Signature + Date: two columns with line on top and label below -->
          <div style="display:flex;justify-content:space-between;gap:24px;margin-top:16px;">
            <div style="width:48%;">
              <div style="border-bottom:1px solid #000;height:1px;margin:0 0 8px 0;"></div>
              <div style="font-size:14px;">Signature of Subject</div>
            </div>
            <div style="width:48%;">
              <div style="border-bottom:1px solid #000;height:1px;margin:0 0 8px 0;"></div>
              <div style="font-size:14px;">Date</div>
            </div>
          </div>
        </div>
      </div>
    `
    document.body.appendChild(container)

    // Render signature page to canvas then to PDF
    const canvas = await html2canvas(container, { backgroundColor: '#ffffff', scale: 2, useCORS: true })
    const imgData = canvas.toDataURL('image/png')
    const signaturePdfDoc = new jsPDF({ orientation: 'p', unit: 'pt', format: 'a4' })
    const pdfW = signaturePdfDoc.internal.pageSize.getWidth()
    const pdfH = signaturePdfDoc.internal.pageSize.getHeight()
    signaturePdfDoc.addImage(imgData, 'PNG', 0, 0, pdfW, pdfH)

    // Cleanup temp DOM
    document.body.removeChild(container)

    // If we have consentId, consentVersion, and dispatch, fetch the original PDF and merge
    if (consentId && consentVersion && dispatch) {
      try {
        // Use the same request function as downloadConsentPDF to ensure authentication
        const originalPdfBlob = await new Promise((resolve, reject) => {
          dispatch(
            request({
              method: 'GET',
              url: `control/v2/consent/pdf?consent_id=${consentId}&version=${consentVersion}`,
              resType: 'blob',
              success: (blob) => resolve(blob),
              error: (err) => reject(err)
            })
          )
        })

        if (originalPdfBlob) {
          const originalPdfBytes = await originalPdfBlob.arrayBuffer()
          const originalPdf = await PDFDocument.load(originalPdfBytes)
          const signaturePdfBytes = await signaturePdfDoc.output('arraybuffer')
          const signaturePdf = await PDFDocument.load(signaturePdfBytes)

          // Merge original PDF with signature page
          const mergedPdf = await PDFDocument.create()
          const originalPages = await mergedPdf.copyPages(originalPdf, originalPdf.getPageIndices())
          const signaturePages = await mergedPdf.copyPages(signaturePdf, signaturePdf.getPageIndices())

          // Add all original pages first
          originalPages.forEach(page => mergedPdf.addPage(page))
          // Add signature page at the end
          signaturePages.forEach(page => mergedPdf.addPage(page))

          // Convert to blob and print
          const mergedPdfBytes = await mergedPdf.save()
          const mergedPdfBlob = new Blob([mergedPdfBytes], { type: 'application/pdf' })
          await printMergedPdf(mergedPdfBlob)
          return
        }
      } catch (fetchErr) {
        console.warn('Failed to fetch original PDF, using signature page only:', fetchErr)
      }
    }

    // Fallback: print signature page only
    const signaturePdfBytes = signaturePdfDoc.output('arraybuffer')
    const signaturePdfBlob = new Blob([signaturePdfBytes], { type: 'application/pdf' })
    await printMergedPdf(signaturePdfBlob)
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('Print to sign failed, falling back to window.print()', err)
    window.print()
  }
}

export default {
  mergeAndPrint
}


